import { useState } from 'react';

import { Controller, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import { SendOutlined } from '@mui/icons-material';

import { CardActions, Collapse, useMediaQuery, useTheme } from '@mui/material';

import { EditorContent } from '@tiptap/react';

import { z } from 'zod';

import { useLocalStorage } from 'usehooks-ts';

import { tss } from 'tss-react/mui';

import { Or2Card } from '@/components/Surface/Or2Card';
import { graphql, type DocumentType } from '@/gql';

import { useMutation } from '@/hooks/mutationWithSnackbar';

import { Or2Button } from '@/components/button/Or2Button';

import { EditorMenu } from '@/modules/editor/menu/EditorMenu';

import { useOr2Editor } from '@/modules/editor/hooks/useOr2Editor';

import { POST_COLLECTION_QUERY } from '@/modules/group/noticeboard/PostCollection';

import { Or2BubbleMenu } from '@/modules/editor/bubble-menu/Or2BubbleMenu';

import { NotificationChipInput } from './poster/NotificationChipInput';

const NoticeboardPostSchema = z.object({
  htmlContent: z.string(),
  notifications: z.array(
    z.union([
      z.object({
        _id: z.string(),
        displayName: z.string(),
        type: z.enum(['Group', 'All']),
      }),
      z.object({
        _id: z.string(),
        displayName: z.string(),
        type: z.enum(['User']),
        avatarUser: z.object({
          _id: z.string(),
          firstName: z.string(),
          lastName: z.string(),
          profileImage: z.string().nullable(),
          image: z.string(),
        }),
      }),
    ]),
  ),
  subgroup: z.string().nullable(),
});

export type NoticeboardPostFormData = z.infer<typeof NoticeboardPostSchema>;
export type NoticeboardPostInput = z.input<typeof NoticeboardPostSchema>;

export const CREATE_POST_MUTATION = graphql(`
  mutation CreatePost($data: CreatePostInput) {
    createPost(params: $data) {
      post {
        ...postFields
      }
    }
  }
`);

export const NOTICEBOARD_POSTER_GROUP_FRAGMENT = graphql(`
  fragment noticeboardPosterGroupFields on Group {
    ...notificationChipInputGroupFields
    _id
    machineName
    referenceName
  }
`);

export interface Prefill {
  title: string;
  href: string;
}

const useStyles = tss.create(({ theme }) => {
  return {
    editorContentSmallScreen: {
      minHeight: theme.spacing(6),
    },
  };
});

interface Props {
  group: DocumentType<typeof NOTICEBOARD_POSTER_GROUP_FRAGMENT>;
  prefill?: Prefill;
}

export const NoticeboardPoster = ({ group, prefill }: Props) => {
  const { classes } = useStyles();
  const { _id: groupId, machineName, referenceName } = group;

  const editorContentKey = ['editorContent', groupId ?? ''].join(':');

  const [postHtmlContent, setPostHtmlContent] = useLocalStorage(
    editorContentKey,
    '',
  );
  const [selected, setSelected] = useState(Boolean(prefill));

  const theme = useTheme();
  const smallerScreen = useMediaQuery(theme.breakpoints.down('md'));

  const { control, handleSubmit, setValue, watch, reset } = useForm<
    NoticeboardPostInput,
    unknown,
    NoticeboardPostFormData
  >({
    defaultValues: {
      htmlContent: prefill?.title
        ? `Have a look at this:<br /><a href="${prefill.href}">${prefill.title}</a>`
        : postHtmlContent || '',
      notifications: [],
      subgroup: '',
    },
    resolver: zodResolver(NoticeboardPostSchema),
  });

  const [createPost, { loading }] = useMutation(CREATE_POST_MUTATION, {
    completionMessage: 'Post created',
    onCompleted: () => reset(),
    update(cache, { data }) {
      const newPost = data?.createPost?.post;

      if (!newPost) {
        return;
      }

      cache.updateQuery(
        {
          query: POST_COLLECTION_QUERY,
          variables: {
            machineName: machineName ?? '',
            tagIds: null,
            first: null,
            cursor: null,
          },
        },
        (queryData) => {
          if (!queryData?.group?.postsConnection.edges) {
            return queryData;
          }

          return {
            group: {
              ...queryData.group,
              __typename: 'Group' as const,
              postsConnection: {
                ...queryData.group.postsConnection,
                __typename: 'GroupPostsConnection' as const,
                edges: [
                  {
                    __typename: 'GroupPostsConnectionEdge' as const,
                    node: newPost,
                  },
                  ...queryData.group.postsConnection.edges,
                ],
              },
            },
          };
        },
      );
    },
  });

  const editor = useOr2Editor({
    htmlContent: watch('htmlContent'),
    editable: !loading,
    variant: 'padded',
    placeholder: `Type here to message${
      referenceName ? ` the ${referenceName}` : ''
    }`,
    onChange: (content) => {
      setPostHtmlContent(content);
      setValue('htmlContent', content);
    },
    onFocus: () => setSelected(true),
  });

  const onSubmit = async (data: NoticeboardPostFormData) => {
    if (!groupId) {
      return;
    }

    await createPost({
      variables: {
        data: {
          htmlContent: data.htmlContent,
          editorContent: null,
          content: null,
          embeds: null,
          notifications: data.notifications.map(({ _id, type }) => ({
            target: _id,
            targetType: type,
          })),
          title: null,
          group: groupId,
          subgroup: data.subgroup,
        },
      },
    });

    setPostHtmlContent('');
    setValue('htmlContent', '');
  };

  const rootOwner = {
    _id: groupId ?? '',
    type: 'Group' as const,
    machineName: machineName ?? '',
  };

  return (
    <Or2Card selected={selected} sx={{ display: 'flex' }}>
      <Or2BubbleMenu editor={editor} rootOwner={rootOwner} />
      <EditorContent
        editor={editor}
        // TODO: Do this better later
        // This is just to handle placeholder text on smaller screens
        className={smallerScreen ? classes.editorContentSmallScreen : ''}
      />
      <Collapse in={selected}>
        <CardActions>
          <EditorMenu
            editor={editor}
            disableFileSelector={false}
            rootOwner={rootOwner}
          />
        </CardActions>
        <CardActions>
          <Controller
            name="notifications"
            control={control}
            render={({ field, fieldState }) => (
              <NotificationChipInput
                group={group}
                selectedOptions={field.value}
                loading={loading}
                textFieldProps={{
                  error: Boolean(fieldState.error),
                  helperText: fieldState.error?.message,
                }}
                {...field}
              />
            )}
          />
          <Or2Button
            disabled={loading || editor?.isEmpty}
            size="large"
            endIcon={<SendOutlined />}
            onClick={handleSubmit(onSubmit)}
          >
            Send
          </Or2Button>
        </CardActions>
      </Collapse>
    </Or2Card>
  );
};
